import React, { useState, useCallback, useEffect } from 'react';
import { Handle, Position, type NodeProps, useUpdateNodeInternals, useEdges } from '@xyflow/react';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { ImageLightbox } from '../ImageLightbox';
import { VideoPlayer } from './VideoPlayer';
import { useNodeResize } from './useNodeResize';
import { Link2, ExternalLink, X, Zap, RefreshCw } from 'lucide-react';

interface TemplateNodeDataProps {
  template_id?: string;
  templateName?: string;
  templateDescription?: string;
  sourceCanvasId?: string;
  sourceCanvasName?: string;
  status?: 'idle' | 'generating' | 'complete' | 'error';
  imageUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  textContent?: string;
  mediaType?: 'image' | 'video' | 'text';
  error?: string;
  nodeWidth?: number;
  onUpdate?: (id: string, updates: any) => void;
  onDelete?: (id: string) => void;
  onWidthChange?: (id: string, width: number) => void;
  onGenerateTemplate?: (id: string) => void;
}

export function TemplateNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as TemplateNodeDataProps;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const updateNodeInternals = useUpdateNodeInternals();
  const edges = useEdges();

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth,
    minWidth: 220,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  const status = data.status || 'idle';
  const isGenerating = status === 'generating';
  const mediaType = data.mediaType || 'image';
  const isVideo = mediaType === 'video';
  const isText = mediaType === 'text';
  const hasOutput = status === 'complete' && (data.imageUrl || data.videoUrl || data.textContent);

  // Check if any nodes are connected to this template node
  const hasConnectedInputs = edges.some(e => e.target === id);

  // Update node internals when content changes
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setTimeout(() => updateNodeInternals(id), 50);
    });
    return () => cancelAnimationFrame(raf);
  }, [id, status, data.imageUrl, data.videoUrl, data.textContent, updateNodeInternals]);

  // Trigger generation
  const handleGenerate = useCallback(() => {
    if (data.onGenerateTemplate && !isGenerating) {
      data.onGenerateTemplate(id);
    }
  }, [id, data, isGenerating]);

  // Handle text content edits (for text output)
  const handleTextContentChange = useCallback((newText: string) => {
    if (data.onUpdate) {
      data.onUpdate(id, { data: { ...data, textContent: newText } });
    }
  }, [id, data]);

  // Colors — distinct blue/indigo for templates
  const headerBg = 'bg-indigo-50/80 border-indigo-100';
  const headerText = 'text-indigo-700';
  const headerIcon = 'text-indigo-600';
  const handleColor = '!bg-indigo-400';

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-md min-w-[220px] relative ${!nodeWidth ? 'max-w-[320px]' : ''} ${
        selected ? 'border-indigo-400' : status === 'error' ? 'border-error-300' : 'border-indigo-200'
      }`}
      style={nodeWidth ? { width: nodeWidth } : undefined}
    >
      {/* Header */}
      <div className={`px-3 py-2 border-b rounded-t-lg flex items-center gap-2 ${status === 'error' ? 'bg-error-50 border-error-100' : headerBg}`}>
        <Link2 className={`w-4 h-4 ${status === 'error' ? 'text-error-600' : headerIcon} ${isGenerating ? 'animate-pulse' : ''}`} />
        <span className={`text-xs font-semibold ${status === 'error' ? 'text-error-700' : headerText} truncate`}>
          {data.templateName || 'Template'}
        </span>
        {selected && (
          <button
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
            className="ml-auto text-gray-400 hover:text-red-500 p-0.5"
            aria-label="Delete template node"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="p-3">
        {/* Template info */}
        {data.templateDescription && (
          <p className="text-xs text-gray-500 mb-2 line-clamp-2">{data.templateDescription}</p>
        )}

        {/* Source canvas link */}
        {data.sourceCanvasName && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
            <ExternalLink className="w-3 h-3" />
            <span className="truncate font-mono">Source: {data.sourceCanvasName}</span>
          </div>
        )}

        {/* Output display */}
        {hasOutput ? (
          <div>
            {data.textContent ? (
              <div className="mb-2">
                <textarea
                  className="w-full min-h-[80px] max-h-[200px] text-sm p-2 border border-gray-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-400 nodrag"
                  value={data.textContent}
                  onChange={(e) => handleTextContentChange(e.target.value)}
                  placeholder="Generated text..."
                />
              </div>
            ) : data.videoUrl ? (
              <VideoPlayer
                videoUrl={data.videoUrl}
                thumbnailUrl={data.thumbnailUrl}
                className="w-full h-[140px] rounded-md mb-2"
                compact
              />
            ) : data.imageUrl ? (
              <img
                src={data.imageUrl}
                alt="Template result"
                className="w-full h-auto rounded-md mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setLightboxOpen(true)}
              />
            ) : null}
            {/* Show hint if no inputs connected */}
            {!hasConnectedInputs && (
              <div className="mb-2 px-2 py-1.5 bg-accent-50 border border-accent-200 rounded text-xs text-accent-700 text-center">
                Connect prompts or images to regenerate
              </div>
            )}
            <Button
              variant="outline"
              size="xs"
              fullWidth
              onClick={handleGenerate}
              disabled={!hasConnectedInputs}
            >
              <RefreshCw className="w-3 h-3" /> Regenerate
            </Button>
          </div>
        ) : status === 'generating' ? (
          <div className="flex flex-col items-center justify-center h-[80px]">
            <Spinner size="md" className="mb-2" />
            <span className="text-xs text-gray-500">
              {isText ? 'Generating text...' : isVideo ? 'Generating video...' : 'Generating...'}
            </span>
          </div>
        ) : status === 'error' ? (
          <div className="text-center">
            <p className="text-xs text-error-600 mb-2">{data.error || 'Generation failed'}</p>
            {!hasConnectedInputs && (
              <div className="mb-2 px-2 py-1.5 bg-accent-50 border border-accent-200 rounded text-xs text-accent-700 text-center">
                Connect prompts or images to retry
              </div>
            )}
            <Button variant="danger" size="xs" fullWidth onClick={handleGenerate} disabled={!hasConnectedInputs}>
              Retry
            </Button>
          </div>
        ) : !data.template_id ? (
          <div className="text-center py-4">
            <p className="text-xs text-gray-400">No template selected</p>
          </div>
        ) : (
          <div>
            {/* Connection hint when no inputs connected */}
            {!hasConnectedInputs ? (
              <div className="px-2 py-3 bg-accent-50 border border-accent-200 rounded text-xs text-accent-700 text-center">
                Connect prompts or images to generate
              </div>
            ) : (
              <Button
                variant="primary"
                size="xs"
                fullWidth
                onClick={handleGenerate}
                leftIcon={<Zap className="w-3.5 h-3.5" />}
              >
                Generate
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Handles: target on left (for additional prompts/images), source on right (for chaining) */}
      <Handle type="target" position={Position.Left} id="template-target" className={`!w-3 !h-3 ${handleColor}`} />
      <Handle type="source" position={Position.Right} id="template-source" className={`!w-3 !h-3 ${handleColor}`} />
      <div {...resizeHandleProps} />
      {lightboxOpen && data.imageUrl && (
        <ImageLightbox src={data.imageUrl} alt="Template result" onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}
